import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType, JWTContextType } from './types';
import { getLogin, getCurrentAdmin } from '../api'

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      
      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        
        try {
          const response: any = await getCurrentAdmin();
          if (response.code === 0 || response.code === 200) {
            const user = response.data;
            localStorage.setItem('user', JSON.stringify(user));
            dispatch({
              type: Types.INITIAL,
              payload: { isAuthenticated: true, user },
            });
            return;
          }
        } catch (e) {
          console.warn('getCurrentAdmin failed, falling back to local user storage');
        }

        // Fallback to local storage
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: !!user,
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: { isAuthenticated: false, user: null },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (account: string, password: string) => {
    const response: any = await getLogin({
      email: account,
      password,
    });
    
    if (response.code !== 0 && response.code !== 200) {
      throw new Error(response.message || "登录失败");
    }
    
    const accessToken = response.data.access_token || response.data.token;
    const refreshToken = response.data.refresh_token;
    let user = response.data.user;

    if (!accessToken) {
      throw new Error("登录成功但未返回 Token");
    }
    
    setSession(accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    
    try {
      const profileRes: any = await getCurrentAdmin();
      if (profileRes.code === 0 || profileRes.code === 200) {
        user = profileRes.data;
      }
    } catch (e) {
      console.warn('Optional profile fetch failed');
    }
    
    if (user) localStorage.setItem('user', JSON.stringify(user));
    
    dispatch({
      type: Types.LOGIN,
      payload: {
        user: user || { email: account }, // Fallback to basic info
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const response = await axios.post('/api/account/register', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      const { access_token: accessToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);

      dispatch({
        type: Types.REGISTER,
        payload: {
          user,
        },
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      loginWithGoogle: () => {},
      loginWithGithub: () => {},
      loginWithTwitter: () => {},
      register,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout, register]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
